// Package imports:
import React, { useState } from 'react';
import cx from 'classnames';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { faEye, faEyeSlash, faSearch, faCircleXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Service imports:
import { getCurrencySymbol } from '../../services/utils';
import Cleave from 'cleave.js/react';

type InputType = 'input' | 'numberFormat' | 'textArea' | 'password' | 'ssnFormat' | 'expirationFormat' | 'creditCardFormat' | 'cvcFormat';
type InputSizes = 'sm' | 'lg';
type TextAlign = 'left' | 'center' | 'right';

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;
type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
interface IOwnProps {
    inputType?: InputType,
    inputSize?: InputSizes,
    textAlign?: TextAlign,
    label?: string,
    shortLabel?: string,
    visible?: boolean,
    toggleVisible?(): void,
    currency?: string,
    withSearchGlassIcon?: boolean,
    clearInput?: boolean,
    error?: string;
    fullWidth?: boolean;
};

type Props = IOwnProps & NumericFormatProps & InputProps & TextAreaProps;

const Input: React.FC<Props> = ({
    // Custom props
    inputType = 'input',
    inputSize = 'lg',
    textAlign = 'left',
    label,
    shortLabel,
    currency,
    error,
    fullWidth = false,

    // Element props
    id,
    className,
    style,
    onFocus,
    onBlur,
    value,
    onChange,

    visible,
    toggleVisible,
    withSearchGlassIcon,
    clearInput,
    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const focus = () => setIsFocused(true);
    const blur = () => setIsFocused(false);

    const [showPassword, setShowPassword] = useState(false);
    const togglePassword = () => setShowPassword(!showPassword);

    // Helper function to check if input has a value
    const hasValue = value !== undefined && value !== null && value.toString().length > 0;

    // Handler to clear the input
    const handleClearInput = () => {
        // Create a synthetic event to trigger onChange with empty value
        if (onChange) {
            const syntheticEvent = {
                target: { value: '' },
                currentTarget: { value: '' }
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(syntheticEvent);
        }
    };

    // Props that overwrite native element props.
    const elementProps = {
        id,
        value,
        onChange,
        style: {
            ...style,
            textAlign: textAlign
        },
        onFocus: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement, Element>) => {
            if (onFocus) onFocus(e);
            focus();
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement, Element>) => {
            if (onBlur) onBlur(e);
            blur();
        }
    }

    const getInput = () => {
        switch (inputType) {
            case 'numberFormat':
                return <>
                    <NumericFormat
                        {...elementProps}
                        {...(props as NumericFormatProps)}
                    />
                    {currency &&
                        <span className={cx('currency', currency)}>
                            {getCurrencySymbol(currency)}
                        </span>
                    }
                </>
            case 'input':
                return <>
                    <input
                        {...elementProps}
                        {...(props as InputProps)}
                    />
                    {/* Show search icon only when clearInput is enabled and there's no value, or when clearInput is disabled */}
                    {withSearchGlassIcon && (!clearInput || !hasValue) && (
                        <FontAwesomeIcon className='input-icon search-icon' icon={faSearch} />
                    )}
                    {/* Show clear button only when clearInput is enabled and there's a value */}
                    {clearInput && hasValue && (
                        <FontAwesomeIcon
                            className='input-icon clear-icon'
                            icon={faCircleXmark}
                            onClick={handleClearInput}
                        />
                    )}
                </>
            case 'password':
                return <>
                    <input
                        type={showPassword ? 'text' : 'password'}
                        {...elementProps}
                        {...(props as InputProps)}
                    />
                    {
                        (elementProps.value !== '') && <>
                            <FontAwesomeIcon icon={faEyeSlash} className={cx("icon-fa", "eye-icon", { 'visible': !showPassword })} onClick={togglePassword} />
                            <FontAwesomeIcon icon={faEye} className={cx("icon-fa", "eye-icon", { 'visible': showPassword })} onClick={togglePassword} />
                        </>
                    }
                </>
            case 'ssnFormat': return <>
                <Cleave
                    {...elementProps}
                    {...(props as InputProps)}
                    options={{ delimiters: ['-'], blocks: [6, 4], numericOnly: true }}
                />
            </>
            case 'expirationFormat': return <>
                <Cleave
                    {...elementProps}
                    {...(props as InputProps)}
                    options={{ date: true, datePattern: ['m', 'y'] }}
                />
            </>
            case 'creditCardFormat': return <>
                <Cleave
                    {...elementProps}
                    {...(props as InputProps)}
                    options={{ creditCard: true, delimiter: '-' }}
                />
            </>
            case 'cvcFormat': return <>
                <Cleave
                    {...elementProps}
                    {...(props as InputProps)}
                    options={{ blocks: [3], numericOnly: true }}
                />
            </>
            default:
                return <textarea
                    {...elementProps}
                    {...(props as TextAreaProps)}
                />
        }
    }

    return (
        <div className={cx('KCL_input', inputSize, className, {
            'is-focused': isFocused,
            'has-value': hasValue,
            'is-disabled': props.disabled,
            'currency_input': currency,
            'has-error': !!error,
            'full-width': fullWidth
        })}>
            {getInput()}
            {label && (
                <label
                    className="input__label"
                    htmlFor={id}
                >
                    <span className="main-label">{label}</span>
                    <span className="short-label">{shortLabel ?? label}</span>
                </label>
            )}
            {error && (
                <div className="input__error-message">
                    {error}
                </div>
            )}
        </div>
    );
}

export default (Input);